<template>
  <v-container>
    <div class="page-header">
      <h1>Dashboard</h1>

      <div class="filter-wrapper">
        <div class="help big">Filter:</div>

        <v-menu :close-on-content-click="true" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn class="default-btn link" v-on="on">
              {{ filterProductText }} <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item
              v-for="option in filterOptions.product"
              :key="option.key"
              @click="activeFilter.product = option.key">
              {{ option.text || option.key }}
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <hr class="header" />
    </div>

    <div v-if="!loading" class="grid-wrapper dashboard-grid">
      <PanelOverview
        :matches="matches"
      />
      <PanelNewLeads
        :organizationId="organizationId"
        :matches="matches"
        @updateLeadStatus="updateLeadStatus"
      />
      <PanelLoansFunded
        :matches="matches"
      />
      <PanelNewReview
        :organizationId="organizationId"
        :review="reviews[0]"
      />

      <PanelLenderReasons
        v-if="$vuetify.breakpoint.lgAndDown"
        :reviews="reviews"
      />

      <div v-if="$vuetify.breakpoint.xl" class="nested-panel-group group-1">
        <PanelLenderReasons
          :reviews="reviews"
        />

        <div class="p-more">
          <h2>More Features coming soon!</h2>
          <p class="strong">
            We want to hear from you! Let us know how we can improve your experience, make a feature suggestion etc.!
          </p>
          <p class="strong">
            e: <a href="email:info@lending-well.com">info@lending-well.com</a>
          </p>
        </div>
      </div>

      <PanelFinancing
        :matches="matches"
      />

      <div v-if="$vuetify.breakpoint.lgAndDown" class="p-more">
        <h2>More Features coming soon!</h2>
        <p class="strong">
          We want to hear from you! Let us know how we can improve your experience, make a feature suggestion etc.!
        </p>
        <p class="strong">
          e: <a href="email:info@lending-well.com">info@lending-well.com</a>
        </p>
      </div>
    </div>

    <Loader v-else></Loader>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import PanelFinancing from './PanelFinancing'
import PanelLenderReasons from './PanelLenderReasons'
import PanelLoansFunded from './PanelLoansFunded'
import PanelNewReview from './PanelNewReview'
import PanelNewLeads from './PanelNewLeads'
import PanelOverview from './PanelOverview'

export default {
  name: 'OrganizationDashboard',
  components: {
    PanelFinancing,
    PanelLenderReasons,
    PanelLoansFunded,
    PanelNewReview,
    PanelNewLeads,
    PanelOverview,
  },
  props: {
    organizationId: String,
  },
  data: () => ({
    products: [],
    matches: [],
    reviews: [],

    activeFilter: {
      product: 'all',
    },
    filterOptions: {
      product: [],
    },

    loading: true,
  }),

  computed: {
    ...mapGetters('feathers', ['apiMatches', 'apiProducts', 'apiReviews']),

    filterProductText () {
      const active = this.filterOptions.product
        .find(({ key }) => key === this.activeFilter.product)

      return active ? active.text : ''
    },
  },

  async mounted () {
    this.products = await this.apiProducts.find({
      query: { organizationId: this.organizationId },
    })

    this.buildFilterOptions()
    this.retrieveApiData()
  },

  methods: {
    async retrieveApiData () {
      this.loading = true

      const query = {
        $sort: { createdAt: -1 },
      }

      query.productId = this.activeFilter.product === 'all'
        ? { $in: this.products.map(({ id }) => id) }
        : this.activeFilter.product

      query.createdAt = {
        $gte: moment().subtract(12, 'months').toISOString(),
      }

      if (query.productId.length || (query.productId.$in && query.productId.$in.length)) {
        this.matches = await this.apiMatches.find({
          query: {
            ...query,
            $scope: ['dashboard'],
          },
        })

        this.reviews = await this.apiReviews.find({
          query: {
            ...query,
            $scope: ['dashboard'],
          },
        })
      }

      this.loading = false
    },

    buildFilterOptions () {
      this.filterOptions.product = [
        { key: 'all', text: 'All Loan Products' },
        ...this.products.map(({ id, internalName, title }) => ({
          key: id, text: internalName || title,
        })),
      ]
    },

    async updateLeadStatus ({ id, status }) {
      try {
        await this.apiMatches.patch(id, { status })

        const index = this.matches.findIndex(match => match.id === id)
        this.matches[index].status = status

        this.addSuccessAlert('Finished updating the status.')
      } catch (error) {
        this.addErrorAlert(`Could not update the status: ${error.message}`)
      }

      this.showStatusDialog = null
    },
  },
}
</script>

<style lang="scss" scoped>
.page-header {
  h1 {
    height: 50px;
    vertical-align: top;
    display: inline-flex;
    align-items: center;
  }

  .filter-wrapper {
    display: inline-block;
  }
}

.dashboard-grid {
  grid-template-rows: auto;
  grid-gap: 35px 30px;

  grid-template-columns: 1fr;
  justify-content: center;

  @include xl {
    grid-template-columns: 375px auto 300px;
  }
}

.dashboard-panel {
  max-width: 100%;
  margin: 0 auto;

  ::v-deep {
    & > div:first-of-type {
      background-color: white;
      border: $borderLight;
      border-radius: 4px;
      height: 100%;
      min-height: 400px;
      max-width: 90vw;
      margin: auto;
      padding-top: 15px;

      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .panel-footer .default-btn.link {
        font-size: 16px;
        text-transform: uppercase;
      }
    }

    h2 {
      display: inline-block;
      margin-bottom: 15px;
      font-size: 18px;
    }
  }
}

.p-overview {
  width: 100%;
  @include xl {
    @include gridPos($colS: 1, $colE: 4);
  }
}

.p-new-leads {
  width: 750px;
  @include xl {
    @include gridPos($colS: 1, $colE: 3);
  }
}

.p-loans-funded {
  width: 300px;
}

.p-new-review {
  width: 375px;
}

.nested-panel-group.group-1 {
  max-width: 100%;
  width: 670px;
  margin: 0 auto;

  @include xl {
    @include gridPos($rowS: 3, $rowE: 5, $colS: 2, $colE: 4);
  }
}

.p-lender-reasons {
  width: 670px;
}

.p-financing {
  height: 512px;
  width: 375px;
  @include xl {
    @include gridPos($rowS: 4, $rowE: 6);
  }
}

.p-more {
  background-color: #F0F0F0;
  border-radius: 4px;

  width: 500px;
  max-width: 90%;
  margin: 40px auto 0;
  padding: 20px 15px;

  h2 {
    font-size: 24px;
    margin-bottom: 15px;
  }

  p {
    margin-bottom: 10px;
  }

  a {
    color: $primaryBlue !important;
    text-decoration: none;

    &:hover { color: rgba($primaryBlue, 0.8) !important; }
  }
}

::v-deep {
  .panel-header {
    display: flex;
    flex-wrap: wrap;

    div {
      flex-grow: 1;
      padding-top: 5px;
      text-align: right;

      p {
        margin-bottom: 5px;
        color: #6E6E6E;
        font-family: "Sansation Regular", sans-serif;

        &:nth-child(2) { font-size: 14px; }
      }
    }
  }

  .panel-content {
    .star-rating-grid {
      margin: 0 0 20px;
    }

    label {
      font-size: 15px;
    }

    p {
      word-break: break-all;
    }
  }

  .panel-footer {
    padding: 15px 10px;
    border-top: $borderLight;
    text-align: center;
  }
}
</style>
